'use strict';
/* ==========================================================================
   levels/jingle.js — step "jingle" (any): STRAIGHT THROUGH ZERO, the 80s synth-pop shop jingle as a notebook lyric
   video (DESIGN.md §5.4). Model: lessons/percent-off/levels/jingle.js (the same pack, the same structure).

   ► The song: assets/music/jingle.m4a (ACE-Step take, assets agent) + assets/music/jingle_words.json
     (tools/song_timing.py): the TRUE lyric lines of content/jingle.json with word times, and the beat grid {bpm,
     offset}. NOTHING here hard-codes a time: acts are found by the lines' SECTIONS (Intro, Verse 1, Chorus, Verse 2,
     Bridge, Chorus, Outro) and every picture beat is keyed to a sung word (S.W(line, word)), so a new take with the
     same lyric sheet needs no code change.

   The whole frame is a pure function of SONG TIME t (the shared <audio> element's clock while it plays; a wall clock
   while audio is locked or missing), so seeking, pausing and the QA screenshots are exact.
   EVERY NUMBER on screen comes from core-local/ratio.js applied to the video's givens (content/script.json): s1
   {c 24, n 2} → p = 12 a pair (the lyric's 1, 2, 3 pairs), s2 {f 5, r 2} → y = 2x + 5 and the no-fee y = 2x, s4
   {r 15; 2, 3, 5 h} → the bridge's table and its (0, 0) row (Ratio.interceptOf). See nums().

   Acts (by section):
     intro    the title; "ratio = y ÷ x"; a doodle line through the origin on the beat
     verse1   pairs → dollars: dots at (1, 12), (2, 24), (3, 36) and the table's ÷ column filling in (12 ÷ 1 = 12 …);
              "times, not plus": the red pen strikes Jay's "+" sticky (24 + 1 = 25?); 3 × 12 = 36
     chorus1  the line draws itself on the beat straight down through the dots to (0, 0) (circled); the same-rate
              steps along it on each word; "y = px" written big (p highlighted), "y = 12x"; proportional ✓
     verse2   $5 to show up + $2 a mile: y = 2x + 5 from (0, 5), "misses zero" (the empty origin circled), not
              proportional; the no-fee line y = 2x; on "x equals one" the point (1, 2) drops a dashed guide: p = 2
     bridge   hours → pay with no 0 row; the ÷ checks (30 ÷ 2 = 15 …); run the pattern back: the "0 | $0" row
              writes in, highlighted
     chorus2  the pay graph: the line through zero on the beat, "y = px", "y = 15x"
     outro    "Straight through zero!", "y = px" big, the three rules card; the PROVEN stamp
   Karaoke (the index card at the bottom): the current line written on word by word in Kalam, the sung word under the
   highlighter, the hook words in red pen, the next line faint below.
   Jay (left) and Ms. Rivera (right) are small line-art figures bobbing on the beat; they point on the key words.
   Controls: click / Space play-pause, ↺ restart (R), ⌂ map, ← → seek 5 s, click or drag the strip at the bottom.
   "Finish" appears once ≥ 90% of the song has been heard; the end of a listen with ≥ 90% heard stamps the step.
   Portrait phone: the stage on top, Dock.karaoke + ↺ ▶ ⌂ + the strip below.
   Long music (docs/WEBSITE.md §8.4): lesson.json preload.stream names the .m4a, so it plays through the shared
   <audio> element (Audio.media), never decoded into memory.

   QA hooks: sc.song (S(i), E(i), W(i, word), beats, sec), sc.plan.acts, sc.nums, sc.debugAt(t) (freeze at song time
   t), sc.now(), sc.play(t), sc.pause(), sc.toggle(), sc.seek(t), sc.restart(), sc.done(), sc.finish(),
   sc.lyricAt(t), sc.activeWord(t), sc.actAt(t), sc.heard, sc.ended, sc.audioReady, sc.canFinish().
   ========================================================================== */
(function () {
  const STEP = 'jingle';
  const AUDIO = 'music/jingle.m4a', WORDS = 'music/jingle_words.json';
  const KEY = 'jingle:zero';
  const PN = NB.PAL, FONT = NB.FONT;
  const BAND_Y = 846, CUR_Y = 934, NEXT_Y = 1012, CUR_SIZE = 62, NEXT_SIZE = 38;
  const TURN = 0.45;                                   // the page turn between acts (s)
  const FINISH_AT = 0.9;                               // share of the song heard before "Finish" / the stamp
  const CREDIT = 'AI-generated jingle · the maths is real';
  const TITLE = 'Straight Through Zero';

  /* ---------------- the numbers (ratio.js only) ---------------- */
  const num = v => Ratio.text(v);                                            // "12", "2.5"
  const dollars = v => (Ratio.isInt(v) ? '$' + Ratio.text(v) : Ratio.money(v));
  // the x values the LYRIC says (verse 1: one, two, three pairs); the verse-2 graph runs 0–4 miles (as the video);
  // the bridge's hours are the video's s4 rows. Every y is computed.
  const V1_XS = [1, 2, 3], V2_XS = [0, 1, 2, 3, 4];
  let NUMS = null;
  function nums() {
    if (NUMS) return NUMS;
    const seg = id => (Game.content.script.segments || []).find(s => s.id === id);
    const q1 = seg('s1').question.given, w2 = seg('s2').question ? seg('s2').question.working.given : null, w4 = seg('s4').question.working.given;
    // verse 1: p = c ÷ n = 12 a pair; rows 1, 2, 3 pairs; Jay's additive slip for 3 pairs (24 + 1)
    const p1 = Ratio.of(q1.c, q1.n), L1 = Ratio.line({ p: p1 });
    const rows1 = V1_XS.map(x => ({ x, y: L1.yq(x), r: Ratio.of(L1.yq(x), x) }));
    const v1 = { p: p1, line: L1, rows: rows1, prop: Ratio.isProportional(rows1.map(r => [r.x, r.y])),
      grid: Ratio.grid(rows1.map(r => [r.x, r.y]), { yStep: Ratio.toNumber(p1) }),
      plusFrom: rows1[1], plus: Ratio.add(rows1[1].y, rows1[2].x - rows1[1].x), times: rows1[2] };
    // verse 2: $5 to show up + $2 a mile (s2's working givens); the no-fee line; the unit rate at x = 1
    const f = w2 ? w2.f : 5, r = w2 ? w2.r : 2;
    const fee = Ratio.line({ p: r, b: f }), nofee = Ratio.line({ p: r });
    const pts2 = V2_XS.map(x => [x, fee.y(x)]);
    const v2 = { f, r, fee, nofee, pts: pts2, grid: Ratio.grid(pts2), prop: Ratio.isProportional(pts2), unit: [1, nofee.y(1)],
      feeB: Ratio.interceptOf(pts2) };
    // bridge + chorus 2: $15 an hour, rows 2, 3, 5 h (s4); the table's own pattern run back to x = 0
    const pay = Ratio.line({ p: w4.r }), xs4 = [w4.a, w4.b, w4.c];
    const rows4 = xs4.map(x => ({ x, y: pay.yq(x), r: Ratio.of(pay.yq(x), x) }));
    const zero = Ratio.interceptOf(rows4.map(q => [q.x, q.y]));
    const back = { dx: -rows4[0].x, dy: Ratio.mul(pay.pq, -rows4[0].x) };            // from the first row back to x = 0
    const br = { r: w4.r, pay, rows: rows4, zero, back, prop: Ratio.isProportional(rows4.map(q => [q.x, q.y]).concat([[0, zero]])),
      grid: Ratio.grid(rows4.map(q => [q.x, q.y]), { yStep: w4.r }) };
    NUMS = { v1, v2, br };
    return NUMS;
  }

  /* ---------------- song timing ---------------- */
  const normW = w => String(w).toLowerCase().replace(/[^a-z0-9]/g, '');
  class Song {
    constructor(d) {
      this.d = d; this.lines = d.lines; this.dur = d.duration || 80;
      const b = d.beats || {};
      this.bpm = b.bpm || 116; this.bt = 60 / this.bpm; this.off = b.offset || 0;
      this.items = d.lines.map(l => ({ ...l, kind: 'line' })).concat((d.extras || []).map((x, j) => ({ ...x, i: 100 + j }))).sort((a, c) => a.s - c.s);
      // the sections as runs of consecutive lines: [{name, lines: [i…]}]
      this.sec = [];
      d.lines.forEach((l, i) => { const last = this.sec[this.sec.length - 1]; if (last && last.name === l.section) last.lines.push(i); else this.sec.push({ name: l.section, lines: [i] }); });
    }
    /* the n-th run of a section (Chorus 1, Chorus 2) → its line indices ([] if absent) */
    section(name, n = 1) { const s = this.sec.filter(x => x.name === name)[n - 1]; return s ? s.lines : []; }
    S(i) { return this.lines[i] ? this.lines[i].s : 1e6; }
    E(i) { return this.lines[i] ? this.lines[i].e : 1e6; }
    /* start of the n-th word of line i that matches `word` (exact, then prefix); the line start if none */
    W(i, word, n = 1) {
      const l = this.lines[i]; if (!l) { if (window.__songCueMiss) window.__songCueMiss.push('?' + i + ':' + word); return 1e6; }
      const k = normW(word);
      for (const pass of [w => normW(w.w) === k, w => normW(w.w).startsWith(k)]) {
        let c = 0;
        for (const w of l.words) if (pass(w) && ++c === n) return w.s;
      }
      if (window.__songCueMiss) window.__songCueMiss.push(i + ':' + word);
      return l.s;
    }
    /* the first match of `word` in any of the lines (the outro's lines differ between takes of the sheet) */
    WA(lines, word) {
      const k = normW(word);
      for (const i of lines) for (const w of this.lines[i].words) if (normW(w.w) === k) return w.s;
      if (window.__songCueMiss) window.__songCueMiss.push(lines.join('/') + ':' + word);
      return lines.length ? this.S(lines[0]) : 1e6;
    }
    beatPos(t) { return (t - this.off) / this.bt; }
    sinceBeat(t) { const p = this.beatPos(t); return (p - Math.floor(p)) * this.bt; }
    pulse(t, k = 9) { return t < this.off - 0.01 ? 0 : Math.exp(-this.sinceBeat(t) * k); }
    singing(t) { return this.lines.some(l => t >= l.s - 0.05 && t <= l.e + 0.1); }
    /* 0 → 1 over n beats starting on the first beat at/after t0, each beat a quick stroke (pure in t) */
    onBeats(t, t0, n) {
      const b0 = Math.ceil(this.beatPos(t0 - 0.12)), tb = this.off + b0 * this.bt;
      if (t < tb) return 0;
      const pos = (t - tb) / this.bt, k = Math.floor(pos);
      if (k >= n) return 1;
      return (k + E.outCubic(Math.min(1, (pos - k) / 0.45))) / n;
    }
  }

  /* ---------------- small drawing helpers ---------------- */
  const SL = new Map();
  const fontOk = () => !document.fonts || document.fonts.status === 'loaded';
  function art(key, bx, by, bw, bh, draw) {
    let sl = SL.get(key); if (!sl) SL.set(key, sl = {});
    if (FX.sprites && fontOk() && !_spriteBaking && _ctm() && spriteIn(sl, bx, by, bw, bh, draw)) return;
    draw();
  }
  const widths = new Map();
  function tw(str, font) {
    const k = font + '|' + str; let w = widths.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) { if (widths.size > 2000) widths.clear(); widths.set(k, w); } }
    return w;
  }
  const hand = (size, w = 700) => `${w} ${size}px ${FONT.hand}`;
  function rrect(x, y, w, h, r) { g.beginPath(); g.moveTo(x + r, y); g.arcTo(x + w, y, x + w, y + h, r); g.arcTo(x + w, y + h, x, y + h, r); g.arcTo(x, y + h, x, y, r); g.arcTo(x, y, x + w, y, r); g.closePath(); }
  function alpha(a, fn) { if (a <= 0.001) return; g.save(); g.globalAlpha *= Math.min(1, a); try { fn(); } finally { g.restore(); } }
  const tag = (text, x, y, t0, o = {}) => Levels.title.priceTag(text, x, y, { t0, rot: o.rot ?? -0.08, size: o.size || 40 });
  const say = (text, x, y, t0, o = {}) => NB.write(text, x, y, { size: o.size || 56, weight: o.weight || 700, align: o.align || 'center', t0, dur: o.dur, color: o.color, font: o.font });
  /* an ink arrow (NB.pen) from a to b, drawn on over [t0, t0 + dur] (bend: a curved arrow) */
  function arrow(ax, ay, bx, by, t0, o = {}) {
    if (T < t0) return;
    const p = E.outCubic(P(T, t0, t0 + (o.dur || 0.35))), col = o.color || PN.ink, w = o.w || 5;
    const mid = o.bend ? [[ax, ay], [(ax + bx) / 2 + o.bend, (ay + by) / 2], [bx, by]] : [[ax, ay], [bx, by]];
    NB.pen(mid, { w, color: col, prog: p, step: 8 });
    if (p >= 0.98) {
      const [qx, qy] = mid[mid.length - 2], a = Math.atan2(by - qy, bx - qx), k = 20;
      NB.pen([[bx - Math.cos(a - 0.5) * k, by - Math.sin(a - 0.5) * k], [bx, by], [bx - Math.cos(a + 0.5) * k, by - Math.sin(a + 0.5) * k]], { w, color: col, step: 4 });
    }
  }
  /* a point label on a small paper backing (x = left, y = middle) so it reads over grid lines */
  function plabel(text, x, y, t0, o = {}) {
    if (T < t0) return;
    const size = o.size || 32, w = tw(text, hand(size)), col = o.color || PN.ink, x0 = o.align === 'right' ? x - w : x;
    alpha(P(T, t0, t0 + 0.2), () => { g.fillStyle = PN.page; rrect(x0 - 8, y - size * 0.62, w + 16, size * 1.24, 8); g.fill(); });
    NB.write(text, x0, y, { size, weight: 700, color: col, t0, dur: 0.3 });
  }
  /* the highlighter behind one part of a centred line of handwriting */
  function hiPart(text, part, cx, y, size, t0, o = {}) {
    const i = text.indexOf(part); if (i < 0) return;
    const f = hand(size), x0 = cx - tw(text, f) / 2 + tw(text.slice(0, i), f), w = tw(part, f);
    NB.highlight(x0 - size * 0.08, y - size * 0.34, w + size * 0.16, size * 0.7, { t0, dur: o.dur || 0.3, color: o.color });
  }

  /* ---------------- graphs: axes in ink over a graph-paper panel (as levels/video.js) ---------------- */
  function graphOf(o) {
    const G = Object.assign({ xStep: 1, yEvery: 1, xEvery: 1, lab: 26 }, o);
    const nv = v => (v && typeof v === 'object' ? Ratio.toNumber(v) : +v);
    G.X = x => G.ox + (nv(x) / G.xStep) * G.ux;
    G.Y = y => G.oy - (nv(y) / G.yStep) * G.uy;
    G.xMax = G.xSq * G.xStep; G.yMax = G.ySq * G.yStep;
    G.right = G.ox + G.xSq * G.ux; G.top = G.oy - G.ySq * G.uy;
    return G;
  }
  function graph(G, t0) {
    if (T < t0) return;
    const w = G.right - G.ox, h = G.oy - G.top;
    alpha(P(T, t0, t0 + 0.3), () => art('jgp|' + G.id, G.ox - 4, G.top - 4, w + 8, h + 8, () => {
      g.fillStyle = PN.page; g.fillRect(G.ox, G.top, w, h);
      g.strokeStyle = PN.gridMajor; g.lineWidth = 2; g.beginPath();
      for (let i = 1; i <= G.xSq; i++) { g.moveTo(G.ox + i * G.ux, G.top); g.lineTo(G.ox + i * G.ux, G.oy); }
      for (let j = 1; j <= G.ySq; j++) { g.moveTo(G.ox, G.oy - j * G.uy); g.lineTo(G.right, G.oy - j * G.uy); }
      g.stroke();
    }));
    const pr = E.outCubic(P(T, t0 + 0.1, t0 + 0.6));
    NB.pen([[G.ox, G.oy], [G.right + 18, G.oy]], { w: 5, color: PN.ink, prog: pr, step: 10 });
    NB.pen([[G.ox, G.oy], [G.ox, G.top - 18]], { w: 5, color: PN.ink, prog: pr, step: 10 });
    const lt = t0 + 0.4;
    NB.write('0', G.ox - 38, G.oy + 16, { font: 'ui', size: G.lab, weight: 700, align: 'right', t0: lt, dur: 0.1 });
    for (let j = G.yEvery; j <= G.ySq; j += G.yEvery) NB.write(num(Ratio.mul(j, G.yStep)), G.ox - 14, G.oy - j * G.uy, { font: 'ui', size: G.lab, weight: 700, align: 'right', t0: lt + j * 0.02, dur: 0.12 });
    for (let i = G.xEvery; i <= G.xSq; i += G.xEvery) NB.write(num(Ratio.mul(i, G.xStep)), G.ox + i * G.ux, G.oy + 26, { font: 'ui', size: G.lab, weight: 700, align: 'center', t0: lt + i * 0.02, dur: 0.12 });
    if (G.xLabel) NB.write(G.xLabel, G.right + 30, G.oy, { font: 'ui', size: G.lab - 2, weight: 700, color: PN.pencil, t0: lt + 0.2, dur: 0.3 });
    if (G.yLabel) { g.save(); g.translate(G.ox - (G.yLabW || 70), (G.top + G.oy) / 2); g.rotate(-Math.PI / 2); NB.write(G.yLabel, 0, 0, { font: 'ui', size: G.lab - 2, weight: 700, color: PN.pencil, align: 'center', t0: lt + 0.2, dur: 0.3 }); g.restore(); }
  }
  /* an ink dot that presses in */
  function dot(G, x, y, t0, o = {}) {
    if (T < t0) return;
    const s = pop(T, t0, 0.25, 1.8), r = (o.r || 11) * s;
    g.fillStyle = o.color || PN.ink; g.beginPath(); g.arc(G.X(x), G.Y(y), r, 0, TAU); g.fill();
  }
  /* the far end of y = p x + b inside the grid (clipped at the top and the right) */
  function farX(G, L, x1 = G.xMax) { let xe = Math.min(x1, G.xMax); if (L.p > 0) xe = Math.min(xe, (G.yMax - L.b) / L.p); return xe; }
  /* y = p x + b from x0 to x1, drawn on from x0 with progress prog (0..1) */
  function glineP(G, L, x0, x1, prog, o = {}) {
    if (prog <= 0) return;
    const xe = farX(G, L, x1);
    g.save(); if (o.dash) g.setLineDash(o.dash);
    NB.pen([[G.X(x0), G.Y(L.p * x0 + L.b)], [G.X(xe), G.Y(L.p * xe + L.b)]], { w: o.w || 6, color: o.color || PN.ink, prog, step: 8, alpha: o.alpha ?? 1 });
    g.restore();
  }
  const gline = (G, L, x0, x1, t0, o = {}) => { if (T >= t0) glineP(G, L, x0, x1, E.outCubic(P(T, t0, t0 + (o.dur || 0.7))), o); };

  /* ---------------- ruled index-card tables (as levels/video.js) ---------------- */
  /* cols: [{head, w, t0}]; rows: [{cells, t0 (one or per cell), slot, color, hi}] */
  function table(x, y, cols, rows, o = {}) {
    const header = o.header || 62, pitch = o.pitch || 64, slots = o.slots || rows.length, w = cols.reduce((a, c) => a + c.w, 0), h = header + slots * pitch + 14;
    NB.indexCard(x, y, w, h, { header, pitch });
    g.save(); g.strokeStyle = PN.cardLine; g.lineWidth = 2; g.beginPath();
    let cx = x; for (let i = 0; i < cols.length - 1; i++) { cx += cols[i].w; if (T >= (cols[i + 1].t0 ?? -1e9)) { g.moveTo(cx, y + 10); g.lineTo(cx, y + h - 10); } }
    g.stroke(); g.restore();
    cx = x;
    const colX = [];
    cols.forEach(c => { colX.push(cx + c.w / 2); if (c.head) NB.write(c.head, cx + c.w / 2, y + header / 2 + 2, { font: 'ui', size: c.hs || 28, weight: 700, align: 'center', t0: c.t0, dur: 0.3 }); cx += c.w; });
    const rowY = k => y + header + k * pitch + pitch / 2;
    rows.forEach((r, i) => {
      const ry = rowY(r.slot ?? i);
      if (r.hi != null) NB.highlight(x + 10, ry - pitch * 0.38, w - 20, pitch * 0.76, { t0: r.hi, dur: 0.35 });
      r.cells.forEach((cell, j) => { if (cell == null) return; const t0 = Array.isArray(r.t0) ? r.t0[j] : r.t0; NB.write(cell, colX[j], ry, { size: o.size || 40, weight: 700, align: 'center', t0, dur: 0.3, color: r.color }); });
    });
    return { x, y, w, h, colX, rowY };
  }

  /* ---------------- the graphs of the song (from the model's grids) ---------------- */
  let GR = null;
  function graphs() {
    if (GR) return GR;
    const N = nums();
    const g1 = N.v1.grid, g2 = N.v2.grid, g4 = N.br.grid;
    GR = {
      v1: graphOf({ id: 'v1', ox: 560, oy: 790, ux: 95, uy: 112, xSq: g1.xSquares, ySq: g1.ySquares, yStep: g1.yStep, xLabel: 'pairs', yLabel: 'dollars' }),
      v2: graphOf({ id: 'v2', ox: 560, oy: 800, ux: 95, uy: 34, xSq: g2.xSquares, ySq: g2.ySquares, yStep: g2.yStep, yEvery: 2, xLabel: 'miles', yLabel: 'dollars' }),
      pay: graphOf({ id: 'pay', ox: 560, oy: 790, ux: 78, uy: 76, xSq: g4.xSquares, ySq: g4.ySquares, yStep: g4.yStep, xLabel: 'hours', yLabel: 'pay ($)' }),
    };
    return GR;
  }

  /* ======================================================================
     act: intro — the title, "ratio = y ÷ x", a doodle line through zero
     ====================================================================== */
  function actIntro(S, t, A) {
    NB.ground();
    NB.title(TITLE, 960, 290, { size: 110, t0: 0.15, hi: 'Zero' });
    const L = A.lines, tR = L.length ? S.W(L[L.length - 1], 'ratios') : 1;
    // the doodle: axes, then a line from the origin drawn on the beat (4 beats from the first line)
    const ox = 640, oy = 740, len = 360;
    const pr = E.outCubic(P(t, 0.3, 0.9));
    NB.pen([[ox, oy], [ox + len, oy]], { w: 5, color: PN.ink, prog: pr, step: 10 });
    NB.pen([[ox, oy], [ox, oy - len * 0.8]], { w: 5, color: PN.ink, prog: pr, step: 10 });
    const lp = S.onBeats(t, L.length ? S.S(L[0]) : 0.5, 4);
    if (lp > 0) NB.pen([[ox, oy], [ox + len * 0.85, oy - len * 0.72]], { w: 6, color: PN.redpen, prog: lp, step: 8 });
    if (t > 0.9) NB.write('0', ox - 36, oy + 16, { font: 'ui', size: 26, weight: 700, align: 'right', t0: 0.9, dur: 0.1 });
    if (lp >= 1) NB.circle(ox, oy, 26, 26, { t0: S.off + Math.ceil(S.beatPos(S.S(L[0] || 0) - 0.12) + 4) * S.bt, dur: 0.35 });
    // "a word about ratios": ratio = y ÷ x
    if (t > tR) {
      say('ratio = y ÷ x', 1300, 520, tR, { size: 76 });
      hiPart('ratio = y ÷ x', 'y ÷ x', 1300, 520, 76, tR + 0.4);
      NB.note('the same every row?', 1300, 610, -0.03, { t0: tR + 0.8, size: 38, align: 'center' });
    }
  }

  /* ======================================================================
     act: verse 1 — the table's ÷ column; "times, not plus"; 3 × 12 = 36
     ====================================================================== */
  const T1 = { x: 1090, y: 240 };
  function verse1Table(S, A, N, t, done) {
    const L = A.lines, v = N.v1, early = -1e9;
    const tT = done ? early : S.W(L[0], 'Twelve'), tO = done ? early : S.W(L[0], 'one'), tTw = done ? early : S.W(L[0], 'twenty'), tTo = done ? early : S.W(L[0], 'two');
    const tD = done ? early : S.W(L[1], 'Divide'), tEv = done ? early : S.W(L[1], 'every'), tTi = done ? early : S.W(L[1], 'time'), tSa = done ? early : S.W(L[1], 'same');
    const tTh = done ? early : S.W(L[3], 'Three'), tTy = done ? early : S.W(L[3], 'thirty'), tKn = done ? early : S.W(L[3], 'know');
    const rows = v.rows.map((r, k) => ({ cells: [num(r.x), dollars(r.y), num(r.r)], t0: [[tO, tTo, tTh][k] - (k === 2 ? 0 : 0.1), [tT, tTw, tTy][k], [tEv, tTi, tKn][k]] }));
    const tb = table(T1.x, T1.y, [{ head: 'pairs', w: 130, t0: done ? early : A.at + 0.1 }, { head: 'dollars', w: 150, t0: done ? early : A.at + 0.1 }, { head: '$ ÷ pairs', w: 200, t0: tD }], rows, { slots: 3, pitch: 66 });
    // the same number every time: the ÷ column highlighted
    if (t > tSa) NB.highlight(T1.x + 290, T1.y + 70, 180, 186, { t0: tSa, dur: 0.4, alpha: 0.5 });
    return tb;
  }
  function actVerse1(S, t, A) {
    NB.ground();
    const N = nums(), v = N.v1, G = graphs().v1, L = A.lines;
    graph(G, A.at + 0.05);
    const tT = S.W(L[0], 'Twelve'), tTw = S.W(L[0], 'twenty'), tTy = S.W(L[3], 'thirty');
    [tT, tTw, tTy].forEach((t0, k) => { const r = v.rows[k]; dot(G, r.x, r.y, t0); plabel(`(${num(r.x)}, ${num(r.y)})`, G.X(r.x) + 18, G.Y(r.y) + 30, t0 + 0.15, { size: 28 }); });
    verse1Table(S, A, N, t, false);
    // "Times, not plus": Jay's "+" sticky (24 + 1 = 25?), struck on "plus"; × 12 on "Times"
    const tTi = S.W(L[2], 'Times'), tPl = S.W(L[2], 'plus'), tGr = S.W(L[2], 'grow');
    const pf = v.plusFrom;
    if (t > S.S(L[2]) - 0.35) {
      const SY = 545, st = NB.sticky(`${dollars(pf.y)} + ${v.times.x - pf.x}\n= ${dollars(v.plus)}?`, 1100, SY, { w: 250, rot: -0.05, t0: S.S(L[2]) - 0.35, size: 40 });
      NB.strike(1112, SY + st.h - 30, 1100 + st.w - 10, SY + 40, { t0: tPl + 0.05, dur: 0.3 });
      NB.note('not plus', 1400, 715, -0.04, { t0: tPl + 0.3, size: 38 });
    }
    if (t > tTi) {
      say(`× ${num(v.p)}`, 1470, 620, tTi, { size: 84 });
      NB.circle(1470, 620, 90, 58, { t0: tGr, dur: 0.35 });
    }
    // "Three pairs at twelve is thirty-six": 3 × 12 = 36
    const tTh = S.W(L[3], 'Three'), tKn = S.W(L[3], 'know');
    if (t > tTh) {
      const eq = `${num(v.times.x)} × ${num(v.p)} = ${num(v.times.y)}`;
      say(eq, 1330, 790, tTh, { size: 60, dur: Math.max(0.4, tTy + 0.3 - tTh) });
      NB.tick(1330 + tw(eq, hand(60)) / 2 + 50, 790, { s: 1.3, t0: tKn });
    }
  }

  /* ======================================================================
     act: chorus — the line through zero on the beat; the same-rate steps; "y = px"; proportional ✓
     ====================================================================== */
  function actChorus(S, t, A) {
    NB.ground();
    const N = nums(), L = A.lines, first = A.n === 1;
    const G = first ? graphs().v1 : graphs().pay, line = first ? N.v1.line : N.br.pay;
    const pts = first ? N.v1.rows.map(r => [r.x, r.y]) : N.br.rows.map(r => [r.x, r.y]).concat([[0, N.br.zero]]);
    graph(G, -1e9);
    pts.forEach(([x, y]) => dot(G, x, y, -1e9));
    const c0 = L[0], c1 = L[1], c2 = L[2], c3 = L[3];
    // "Straight through zero!": the line from the far end straight down through the dots to (0, 0), one stroke a beat
    const tS = S.W(c0, 'Straight'), tZ = S.W(c0, 'zero');
    const xe = farX(G, line), prog = S.onBeats(t, tS, 4);
    if (prog > 0) NB.pen([[G.X(xe), G.Y(line.y(xe))], [G.X(0), G.Y(0)]], { w: 7, color: PN.ink, prog, step: 8 });
    if (t > tZ) {
      NB.circle(G.X(0), G.Y(0), 30, 30, { t0: tZ, dur: 0.35 });
      plabel('(0, 0)', G.X(0) - 30, G.Y(0) - 40, tZ + 0.2, { size: 30, color: PN.redpen, align: 'right' });
    }
    // "The same rate all the way": a step (1 across, p up) on each word, along the line
    const ws = S.lines[c1] ? S.lines[c1].words : [], nSteps = Math.max(0, Math.min(ws.length, Math.floor(xe)));
    for (let k = 0; k < nSteps; k++) {
      const t0 = ws[k].s; if (t < t0) break;
      const p = E.outCubic(P(t, t0, t0 + 0.25));
      NB.pen([[G.X(k), G.Y(line.y(k))], [G.X(k + 1), G.Y(line.y(k))], [G.X(k + 1), G.Y(line.y(k + 1))]], { w: 4, color: PN.redpen, prog: p, step: 6 });
    }
    if (ws.length && t > ws[0].s) {
      const k = 0, ux = G.X(k + 1) + 8, uy = (G.Y(line.y(k)) + G.Y(line.y(k + 1))) / 2;
      plabel(dollars(line.pq), ux, uy, ws[0].s + 0.2, { size: 28, color: PN.redpen });
      const one = first ? '1 pair' : '1 h';
      plabel(one, (G.X(0) + G.X(1)) / 2 - tw(one, hand(24)) / 2, G.Y(0) + 26, ws[0].s + 0.2, { size: 24, color: PN.redpen });
    }
    const unit = first ? 'a pair' : 'an hour';
    if (ws.length && t > ws[0].s) {
      say(`${dollars(line.pq)} ${unit}, every step`, 1330, 300, ws[0].s, { size: 46, dur: Math.max(0.4, ws[ws.length - 1].s + 0.2 - ws[0].s) });
    }
    // "Y equals p x": the hook written big, the p highlighted; this line's p below
    const tY = S.W(c2, 'Y'), tP = S.W(c2, 'p'), tX = S.W(c2, 'x');
    if (t > tY) {
      const hook = 'y = px', f = hand(170), w = tw(hook, f), x0 = 1330 - w / 2, pX = x0 + tw('y = ', f), pw = tw('p', f);
      NB.highlight(pX - 12, 470 - 56, pw + 24, 112, { t0: tP, dur: 0.25 });
      NB.write(hook, x0, 470, { size: 170, weight: 700, t0: tY, dur: Math.max(0.35, tX + 0.25 - tY) });
      if (t > tX) NB.write(`here: y = ${num(line.pq)}x`, 1330, 600, { size: 50, weight: 700, align: 'center', color: PN.pencil, t0: tX + 0.1, dur: 0.4 });
    }
    // "That's proportional, okay!"
    const tPr = S.W(c3, 'proportional'), tOk = S.W(c3, 'okay');
    if (t > tPr) {
      say('proportional', 1310, 720, tPr, { size: 70, color: PN.redpen, dur: Math.max(0.35, tOk - tPr) });
      NB.tick(1310 + tw('proportional', hand(70)) / 2 + 50, 720, { s: 1.6, t0: tOk });
    }
  }

  /* ======================================================================
     act: verse 2 — $5 + $2 a mile misses zero; p at x = 1
     ====================================================================== */
  function actVerse2(S, t, A) {
    NB.ground();
    const N = nums(), v = N.v2, G = graphs().v2, L = A.lines;
    graph(G, A.at + 0.05);
    const tF = S.W(L[0], 'Five'), tTw = S.W(L[0], 'two'), tMi = S.W(L[0], 'mile');
    const tSt = S.W(L[1], 'straight'), tMs = S.W(L[1], 'misses'), tZe = S.W(L[1], 'zero');
    const tP = S.W(L[2], 'P'), tUn = S.W(L[2], 'unit'), tPr = S.W(L[2], 'price'), tOn = S.W(L[2], 'one');
    const tX = S.W(L[3], 'x'), tO1 = S.W(L[3], 'one'), tGr = S.W(L[3], 'graphing'), tDo = S.W(L[3], 'done');
    // the $5 to show up: the dot at (0, 5), a price tag
    const feeA = 1 - 0.65 * P(t, tP, tP + 0.4);                              // the fee line steps back to pencil for p
    alpha(feeA, () => {
      dot(G, 0, v.f, tF);
      gline(G, v.fee, 0, G.xMax, tTw, { dur: Math.max(0.5, tMi + 0.3 - tTw) });
      v.pts.slice(1).forEach(([x, y], k) => dot(G, x, y, tTw + 0.15 * (k + 1), { r: 9 }));
    });
    if (t > tF) tag(`${dollars(v.f)} TO SHOW UP`, 1330, 300, tF, { size: 38, rot: -0.05 });
    if (t > tMi) say(`y = ${num(v.r)}x + ${num(v.f)}`, 1330, 400, tMi, { size: 60 });
    // "a straight line, but it misses zero"
    if (t > tSt) NB.tick(1330 + tw(`y = ${num(v.r)}x + ${num(v.f)}`, hand(60)) / 2 + 44, 400, { s: 1.2, t0: tSt, color: PN.ink });
    if (t > tMs) {
      NB.circle(G.X(0), G.Y(0), 30, 30, { t0: tMs, dur: 0.35 });
      say('misses (0, 0)', 1330, 485, tMs, { size: 48, color: PN.redpen });
    }
    if (t > tZe) {
      say('not proportional', 1330, 550, tZe, { size: 48, color: PN.redpen });
      plabel(`starts at (0, ${num(v.feeB)})`, G.X(0) + 20, G.Y(v.f) - 34, tZe + 0.2, { size: 26, color: PN.pencil });
    }
    // "P is the unit rate, the price for only one": the no-fee line through zero
    if (t > tUn) {
      gline(G, v.nofee, 0, G.xMax, tUn, { dur: 0.8 });
      const xe = farX(G, v.nofee);
      plabel(`no fee: y = ${num(v.r)}x`, G.X(3) + 16, G.Y(v.nofee.y(3)) + 52, tUn + 0.6, { size: 28 });
      say('p = the unit rate', 1330, 640, tUn, { size: 50 });
    }
    if (t > tPr) NB.note('the price for one mile', 1330, 700, -0.02, { t0: tPr, size: 38, align: 'center' });
    // "Find it at x equals one": a dashed guide up from x = 1 to the line, across to the y-axis: (1, p)
    const [ux, uy] = v.unit;
    if (t > tX) {
      const p1 = E.outCubic(P(t, tX, tX + 0.35));
      g.save(); g.setLineDash([12, 10]);
      NB.pen([[G.X(ux), G.Y(0)], [G.X(ux), G.Y(uy)]], { w: 4, color: PN.redpen, prog: p1, step: 6 });
      g.restore();
      NB.circle(G.X(ux), G.Y(0) + 26, 22, 22, { t0: tX, dur: 0.3 });
    }
    if (t > tO1) {
      const p2 = E.outCubic(P(t, tO1, tO1 + 0.3));
      g.save(); g.setLineDash([12, 10]);
      NB.pen([[G.X(ux), G.Y(uy)], [G.X(0), G.Y(uy)]], { w: 4, color: PN.redpen, prog: p2, step: 6 });
      g.restore();
      dot(G, ux, uy, tO1, { color: PN.redpen, r: 12 });
      plabel(`(1, ${num(uy)})`, G.X(ux) + 18, G.Y(uy) + 6, tO1 + 0.15, { size: 30, color: PN.redpen });
    }
    if (t > tGr) {
      const eq = `p = ${num(uy)}`;
      say(eq, 1330, 780, tGr, { size: 64 });
      hiPart(eq, eq, 1330, 780, 64, tGr + 0.2);
      NB.tick(1330 + tw(eq, hand(64)) / 2 + 46, 780, { s: 1.3, t0: tDo });
    }
  }

  /* ======================================================================
     act: bridge — a table with no 0 row; run the pattern back → 0 | $0
     ====================================================================== */
  const TB = { x: 560, y: 230, pitch: 92, header: 66 };
  function actBridge(S, t, A) {
    NB.ground();
    const N = nums(), b = N.br, L = A.lines;
    const tNo = S.W(L[0], 'No'), tZ1 = S.W(L[0], 'zero'), tTa = S.W(L[0], 'table'), tDn = S.W(L[0], 'Don'), tFr = S.W(L[0], 'fret');
    const tRun = S.W(L[1], 'Run'), tBk = S.W(L[1], 'backwards'), tZ2 = S.W(L[1], 'zero'), tBe = S.W(L[1], 'bet');
    const rows = b.rows.map((r, k) => ({ cells: [num(r.x), dollars(r.y)], t0: A.at + 0.15 + 0.12 * k, slot: k + 1 }));
    rows.push({ cells: [num(0), dollars(b.zero)], t0: [tZ2, tZ2 + 0.15], slot: 0, hi: tZ2 + 0.2, color: PN.redpen });
    const tb = table(TB.x, TB.y, [{ head: 'hours', w: 200, t0: A.at + 0.1 }, { head: 'pay', w: 230, t0: A.at + 0.1 }], rows, { slots: 4, pitch: TB.pitch, header: TB.header, size: 48 });
    const y0 = tb.rowY(0), y1 = tb.rowY(1);
    // "No zero in the table?": a pencil "?" in the empty first row
    if (t > tZ1 && t < tZ2) alpha(1 - P(t, tZ2 - 0.2, tZ2), () => say('?', tb.x + tb.w / 2, y0, tZ1, { size: 60, color: PN.pencil }));
    if (t > tZ1) NB.note('no 0 row?', tb.x + tb.w + 30, y0, -0.03, { t0: tZ1 + 0.1, size: 38 });
    // "Run the pattern backwards": the red arrow back up the table to x = 0
    if (t > tBk) {
      arrow(tb.x - 30, y1 + 10, tb.x - 30, y0 - 10, tBk, { color: PN.redpen, dur: 0.4 });
      NB.note(`${num(b.back.dx)} h, ${b.back.dy.n < 0 ? '−' : ''}${dollars(Ratio.mul(b.back.dy, -1))}`, tb.x - 56, (y0 + y1) / 2, -0.05, { t0: tBk + 0.3, size: 34, align: 'right' });
    }
    if (t > tBe) NB.tick(tb.x + tb.w + 40, y0 + 50, { s: 1.4, t0: tBe });
    // the ÷ checks: every row gives the same p
    const checks = b.rows.map(r => `${dollars(r.y)} ÷ ${num(r.x)} = ${num(r.r)}`);
    const CX = 1390;
    [tTa, tDn, tFr].forEach((t0, k) => { if (t > t0) say(checks[k], CX, 330 + 84 * k, t0, { size: 50 }); });
    if (t > tFr) hiPart(checks[2], num(b.rows[2].r), CX, 330 + 84 * 2, 50, tFr + 0.3);
    if (t > tZ2) say('(0, 0): through zero', CX, 640, tZ2 + 0.1, { size: 50, color: PN.redpen });
    if (t > tBe) NB.note('proportional', CX, 720, -0.02, { t0: tBe, size: 44, align: 'center' });
  }

  /* ======================================================================
     act: outro — the hook big; the three rules; PROVEN
     ====================================================================== */
  function actOutro(S, t, A) {
    NB.ground();
    const L = A.lines, last = S.lines.length - 1;
    const tS = S.WA(L, 'Straight'), tPr = S.WA(L, 'proportional'), tEnd = S.E(last);
    if (t > tS - 0.1) {
      NB.title('Straight through zero!', 960, 280, { size: 96, t0: tS - 0.1, hi: 'zero!' });
      const hook = 'y = px', f = hand(170), w = tw(hook, f), x0 = 960 - w / 2, pX = x0 + tw('y = ', f), pw = tw('p', f);
      NB.highlight(pX - 12, 450 - 56, pw + 24, 112, { t0: tS + 0.9, dur: 0.3 });
      NB.write(hook, x0, 450, { size: 170, weight: 700, t0: tS + 0.4, dur: 0.6 });
    }
    if (t > tPr - 0.2) {
      const x = 470, y = 580, w = 980, h = 250;
      const s = E.outBack(P(t, tPr - 0.2, tPr + 0.2), 1.3);
      g.save(); g.translate(x + w / 2, y + h / 2); g.scale(s, s); g.translate(-(x + w / 2), -(y + h / 2));
      NB.indexCard(x, y, w, h, { header: 64, pitch: 60 });
      NB.write('proportional means…', x + 30, y + 34, { font: 'ui', size: 30, weight: 700, t0: tPr, dur: 0.4 });
      ['y ÷ x is the same in every row', 'the graph goes straight through (0, 0)', 'p = the price for one (read it at x = 1)']
        .forEach((r, i) => NB.write(r, x + 40, y + 64 + 60 * i + 30, { size: 38, weight: i === 1 ? 700 : 400, color: i === 1 ? PN.redpen : PN.ink, t0: tPr + 0.3 + i * 0.4, dur: 0.4 }));
      g.restore();
    }
    const tSt = Math.min(S.WA(L, 'okay'), tEnd + 1.2, S.dur - 0.8);     // PROVEN stamps on the last "okay!"
    if (t > tSt) UI.stamp('PROVEN', 1540, 450, { s: E.outBack(P(t, tSt, tSt + 0.3), 1.6), rot: -0.12 });
  }

  /* ---------------- the acts' plan (by section, from the take's own line times) ---------------- */
  function plan(S) {
    const b = i => (i > 0 ? Math.max(S.E(i - 1) - 0.1, S.S(i) - 0.35) : 0);   // never before the previous line's last word
    const acts = [];
    const add = (id, draw, lines, o = {}) => { if (id !== 'intro' && !lines.length) return; acts.push({ id, draw, lines, at: id === 'intro' ? 0 : b(lines[0]), ...o }); };
    add('intro', actIntro, S.section('Intro'));
    add('verse1', actVerse1, S.section('Verse 1'));
    add('chorus1', actChorus, S.section('Chorus', 1), { n: 1 });
    add('verse2', actVerse2, S.section('Verse 2'));
    add('bridge', actBridge, S.section('Bridge'));
    add('chorus2', actChorus, S.section('Chorus', 2), { n: 2 });
    add('outro', actOutro, S.section('Outro'));
    acts.sort((a, c) => a.at - c.at);
    acts.forEach((a, i) => { a.end = i + 1 < acts.length ? acts[i + 1].at : S.dur; });
    return { acts, ticks: acts.slice(1).map(a => a.at) };
  }
  function actAt(p, t) { let k = 0; for (let i = 0; i < p.acts.length; i++) if (t >= p.acts[i].at) k = i; return k; }
  /* the notebook page turn: the old page slides up off the top (the next page is already underneath) */
  function turn(drawOld, drawNew, t, t0, dur) {
    const p = E.inOutCubic(P(t, t0, t0 + dur));
    drawNew();
    if (p >= 1) return;
    const dy = -1100 * p;
    g.save(); g.beginPath(); g.rect(0, 0, 1920, 1080 + dy); g.clip(); g.translate(0, dy); drawOld(); g.restore();
    g.save(); g.fillStyle = 'rgba(29,43,79,0.12)'; g.fillRect(0, 1080 + dy, 1920, 8); g.restore();
  }
  function drawActs(S, p, t) {
    const k = actAt(p, t), A = p.acts[k];
    const run = i => () => { g.save(); p.acts[i].draw(S, t, p.acts[i]); g.restore(); g.globalAlpha = 1; };
    if (k > 0 && t < A.at + TURN) turn(run(k - 1), run(k), t, A.at, TURN);
    else run(k)();
  }

  /* ---------------- the cast: Jay (left) and Ms. Rivera (right), bobbing on the beat ---------------- */
  const POSE_DX = { rivera_point: -131.5 / 1100, jay_point: 48.5 / 1100 };     // as levels/video.js (the body stays put)
  function puppet(name, x, y, h, pose, facing, env, t, rot) {
    const img = (Puppet.POSES[name] || {})[pose] || Puppet.DEFAULT[name] || name, native = Puppet.NATIVE[img] || 0;
    const flip = native !== 0 && Math.sign(native) !== Math.sign(facing);
    Puppet.draw(name, x - (POSE_DX[img] || 0) * h * (flip ? -1 : 1), y, h, { env, facing, t, pose, rot });
  }
  function pointCues(S, p) {
    if (p._pts) return p._pts;
    const jay = [], riv = [], A = id => p.acts.find(a => a.id === id), L = id => (A(id) ? A(id).lines : []);
    for (const id of ['chorus1', 'chorus2']) { const l = L(id); if (l.length >= 4) { jay.push(S.W(l[0], 'zero'), S.W(l[2], 'Y')); riv.push(S.W(l[0], 'zero'), S.W(l[3], 'proportional')); } }
    const v1 = L('verse1'), v2 = L('verse2'), br = L('bridge'), out = L('outro');
    if (v1.length >= 4) { jay.push(S.W(v1[0], 'Twelve'), S.W(v1[2], 'plus')); riv.push(S.W(v1[1], 'Divide'), S.W(v1[2], 'Times')); }
    if (v2.length >= 4) { jay.push(S.W(v2[0], 'Five')); riv.push(S.W(v2[1], 'misses'), S.W(v2[2], 'unit'), S.W(v2[3], 'x')); }
    if (br.length >= 2) { jay.push(S.W(br[0], 'zero')); riv.push(S.W(br[1], 'backwards')); }
    if (out.length) { jay.push(S.WA(out, 'Straight')); riv.push(S.WA(out, 'Straight')); }
    return (p._pts = { jay, riv });
  }
  function cast(S, p, t, docked) {
    const pu = S.pulse(t, 7), bar = Math.floor(S.beatPos(t) / 4), sway = (bar % 2 ? 1 : -1) * 0.02, bob = 10 * pu;
    const cues = pointCues(S, p), near = arr => arr.some(x => t > x && t < x + 1.0);
    const y = docked ? 1080 : BAND_Y + 20, h = docked ? 380 : 330;
    puppet('jay', 250, y - bob, h, near(cues.jay) ? 'point' : null, 1, 0.15 + 0.85 * pu, t, sway);
    puppet('rivera', 1740, y - bob, h * 1.05, near(cues.riv) ? 'point' : null, -1, 0.15 + 0.85 * pu, t, -sway);
  }

  /* ---------------- karaoke ---------------- */
  /* the hook words: every word of "Straight through zero!" and "Y equals p x" (red pen) */
  const HOOKS = [['straight', 'through', 'zero'], ['y', 'equals', 'p', 'x']];
  function hookIdx(item) {
    const ws = item.words, out = new Set();
    if (item.section !== 'Chorus' && item.section !== 'Outro') return out;
    for (const h of HOOKS) for (let k = 0; k + h.length <= ws.length; k++) if (h.every((w, j) => normW(ws[k + j].w) === w)) h.forEach((_, j) => out.add(k + j));
    return out;
  }
  const layouts = new Map();
  function layout(item, size) {
    const key = item.i + '|' + item.s + '|' + size;
    if (layouts.has(key)) return layouts.get(key);
    const f = hand(size), sp = tw(' ', f), hk = hookIdx(item);
    let x = 0;
    const toks = item.words.map((w, j) => { const ww = tw(w.w, f), tk = { text: w.w, s: w.s, e: w.e, x, w: ww, hook: hk.has(j) }; x += ww + sp; return tk; });
    const out = { toks, width: x - sp, size };
    if (fontOk()) layouts.set(key, out);
    return out;
  }
  const wordEnd = (ws, j) => { const nx = ws[j + 1]; return Math.min(Math.max(ws[j].e, ws[j].s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); };
  function drawCurrent(item, t, y, maxW = 1640) {
    let size = CUR_SIZE, Lo = layout(item, size);
    if (Lo.width > maxW) { size = Math.floor(size * maxW / Lo.width); Lo = layout(item, size); }
    const x0 = 960 - Lo.width / 2, appear = item.appear ?? item.s - 0.5, a = E.outCubic(P(t, appear, appear + 0.25));
    const yy = y + (1 - a) * 30;
    Lo.toks.forEach((tk, j) => {
      const x = x0 + tk.x, sung = t >= tk.s, active = t >= tk.s - 0.02 && t < wordEnd(Lo.toks, j);
      if (active) NB.highlight(x - 8, yy - size * 0.42, tk.w + 16, size * 0.84, { t0: tk.s - 0.02, dur: 0.12 });
      if (!sung || t < tk.s + 0.3) NB.write(tk.text, x, yy, { size, weight: 700, color: PN.pencilLight, alpha: a });
      if (sung) NB.write(tk.text, x, yy, { size, weight: 700, color: tk.hook ? PN.redpen : PN.ink, t0: tk.s, dur: clamp(tk.e - tk.s, 0.08, 0.3) });
    });
  }
  /* a line comes up 0.5 s before its first word, but never before the previous line has been sung */
  const appearAt = (it, k) => (k === 0 ? it[0].s - 0.5 : Math.min(it[k].s, Math.max(it[k].s - 0.5, it[k - 1].e + 0.05)));
  function lyricAt(song, t) {
    const it = song.items;
    let cur = -1;
    for (let k = 0; k < it.length; k++) if (t >= appearAt(it, k)) cur = k;
    let c = cur >= 0 ? it[cur] : null;
    if (c) c.appear = appearAt(it, cur);
    const nx = it[cur + 1] || null;
    if (c && t > c.e + 2.2 && (!nx || nx.s - t > 1.2)) c = null;       // a long instrumental gap: clear the line
    return { cur: c, next: nx && nx.s - t < 8 ? nx : null };
  }
  /* the word being sung at t (QA: the karaoke's active word) */
  function activeWord(song, t) {
    const { cur } = lyricAt(song, t); if (!cur) return null;
    const ws = cur.words;
    for (let j = 0; j < ws.length; j++) if (t >= ws[j].s - 0.02 && t < wordEnd(ws, j)) return { line: cur.i, j, w: ws[j].w, s: ws[j].s, e: ws[j].e };
    return null;
  }
  function band(song, t) {
    NB.indexCard(40, BAND_Y, 1840, 260, { header: 44, pitch: 78 });
    const { cur, next } = lyricAt(song, t);
    const sec = (cur || next || {}).section;
    if (sec) NB.write(sec.toUpperCase(), 70, BAND_Y + 23, { font: 'ui', size: 22, weight: 700, color: PN.pencil });
    if (cur) drawCurrent(cur, t, CUR_Y);
    if (next) {
      let size = NEXT_SIZE; const w = tw(next.text, hand(size, 400));
      if (w > 1640) size = Math.floor(size * 1640 / w);
      NB.write(next.text, 960, NEXT_Y, { size, weight: 400, color: PN.pencil, align: 'center', alpha: 0.8 });
    } else if (song.items.length && t > song.items[song.items.length - 1].e) {
      NB.write('Straight through zero: y = px.', 960, NEXT_Y, { size: 40, weight: 700, color: PN.pencil, align: 'center', t0: song.items[song.items.length - 1].e + 0.3 });
    }
    if (!cur && !next && t > 0.6) {
      const b = ((Math.floor(song.beatPos(t)) % 3) + 3) % 3, pu = song.pulse(t, 6);
      ['♪', '♫', '♪'].forEach((n, i) => { g.save(); g.font = `64px 'Segoe UI Symbol', 'DejaVu Sans', sans-serif`; g.fillStyle = PN.ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(n, 860 + i * 100, CUR_Y - 22 * pu * (i === b ? 1 : 0.15)); g.restore(); });
    }
  }

  /* ======================================================================
     the scene
     ====================================================================== */
  class Jingle extends Scene {
    enter() {
      this.song = null; this.plan = null; this.err = null;
      this.playing = false; this.frozen = false; this.ended = false; this.startedOnce = false;
      this.pos = 0; this.t0ctx = null; this.heard = 0;
      this.audioReady = false; this.completed = false; this.gone = false;
      Audio.stopMusic();
      try { this.nums = nums(); } catch (e) { this.err = 'ratio: ' + e.message; console.warn('[jingle]', this.err); }
      const wurl = Assets.url(WORDS);
      const words = (window.Loader ? Loader.json(wurl).then(d => d || Promise.reject(new Error('no timings'))) : fetch(wurl).then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))))
        .then(d => { this.song = new Song(d); this.plan = plan(this.song); })
        .catch(e => { this.err = String(e && e.message); console.warn('[jingle] no timings', this.err); });
      const audio = (Audio.has(KEY) ? Promise.resolve() : Audio.load({ [KEY]: Assets.url(AUDIO) })).then(() => { this.audioReady = Audio.has(KEY); }, e => { console.warn('[jingle] no audio', e && e.message); });
      Promise.all([words, audio]).then(() => { if (!this.gone && !this.frozen && !this.startedOnce && this.song) this.play(0); });
      if (window.Device) {
        this.onHidden = Device.on('hidden', () => { if (this.playing && !this.gone) { this.pause(); this.resumeOnReturn = true; } });
        this.onResume = Device.on('resume', () => { if (this.resumeOnReturn && !this.gone && Game.current() === this) { this.resumeOnReturn = false; this.play(this.pos); } });
      }
    }
    exit() {
      this.gone = true; Audio.stopGroup('song'); this.playing = false;
      if (this.media) this.media.pause();
      if (window.Device) { Device.off('hidden', this.onHidden); Device.off('resume', this.onResume); }
    }
    get key() { return KEY; }
    back() { Game.replace(new Levels.map()); }
    /* the song clock: the <audio> element while it plays (t0ctx != null = "on the audio clock"), else pos */
    now() {
      if (this.playing && this.t0ctx != null) {
        const m = this.media;
        if (m) return m.playing() ? m.time() : m.el && m.el.ended ? this.song.dur : this.pos;
        if (Audio.running()) return Audio.now() - this.t0ctx;
      }
      return this.pos;
    }
    play(from = this.now()) {
      if (!this.song) return;
      this.startedOnce = true; this.frozen = false; this.ended = false;
      from = clamp(from, 0, this.song.dur - 0.05);
      Audio.stopGroup('song');
      this.pos = from; this.playing = true; this.t0ctx = null;
      if (!this.audioReady || window.__timeScale > 1) return;
      this.media = this.media || Audio.media(KEY);
      if (this.media) {
        const m = this.media, tok = this.playTok = (this.playTok || 0) + 1;
        this.playAt = Game.time; this.blocked = false;
        m.play(from).then(ok => {
          if (tok !== this.playTok || this.gone) return;
          if (ok && this.playing) { this.pos = m.time(); this.t0ctx = 0; }
          else if (!ok) { this.pause(); this.blocked = true; }
        });
      } else if (Audio.running()) {
        const at = Audio.now() + 0.06;
        Audio.schedule(KEY, { at, offset: from, gain: 1, group: 'song', bus: 'voice', fade: 0.03 });
        this.t0ctx = at - from;
      }
    }
    pause() {
      this.pos = this.now(); this.playing = false; this.t0ctx = null; this.playTok = (this.playTok || 0) + 1;
      Audio.stopGroup('song');
      if (this.media) this.media.pause();
    }
    toggle() { if (this.playing) this.pause(); else this.play(this.ended ? 0 : this.pos); Audio.play('paper', { gain: 0.25 }); }
    seek(t) { if (!this.song) return; const was = this.playing; this.pause(); this.pos = clamp(t, 0, this.song.dur); this.ended = false; if (was) this.play(this.pos); }
    restart() { this.heard = 0; this.play(0); }
    /* QA: freeze on song time t (no pause overlay) */
    debugAt(t) { this.pause(); this.frozen = true; this.startedOnce = true; this.ended = false; this.pos = t; }
    /* stamp the step now (QA / integration; ≥ 90% heard does this at the end, or the Finish button) */
    done() { if (this.completed) return; this.completed = true; this.pause(); if (!Game.progress.stamps[STEP]) Game.complete(STEP); }
    canFinish() { return !!this.song && !this.completed && this.heard >= FINISH_AT * this.song.dur; }
    finish() { if (this.canFinish()) { Audio.play('stamp', { gain: 0.6 }); this.done(); } }
    lyricAt(t) { return this.song ? lyricAt(this.song, t) : null; }
    activeWord(t) { return this.song ? activeWord(this.song, t) : null; }
    actAt(t) { return this.plan ? this.plan.acts[actAt(this.plan, t)].id : null; }

    update(dt) {
      if (!this.song || !this.playing || this.scrub) return;
      if (this.t0ctx == null) {
        this.pos += dt * (window.__timeScale || 1);
        const retry = this.audioReady && !(window.__timeScale > 1) &&
          (this.media ? !this.blocked && Game.time - (this.playAt || 0) > 3 : Audio.running());
        if (retry) this.play(this.pos);
      }
      this.heard += dt * (this.t0ctx == null ? (window.__timeScale || 1) : 1);
      if (this.now() >= this.song.dur - 0.05) {
        this.pause(); this.pos = this.song.dur; this.ended = true;
        if (!this.completed && this.heard >= FINISH_AT * this.song.dur) this.done();
      }
    }
    keyDown(k) {
      if (!this.song) return;
      if (k === ' ') this.toggle();
      else if (k === 'r' || k === 'R') this.restart();
      else if (k === 'ArrowRight') this.seek(this.now() + 5);
      else if (k === 'ArrowLeft') this.seek(this.now() - 5);
      else if (k === 'Enter' && this.ended) this.back();
    }
    stripZone() { return Math.max(32, UI.tap(32)); }
    pointerDown(x, y) {
      if (!this.song || this.ended) return;
      if (UI.docked()) { this.toggle(); return; }
      if (UI.inHud(x, y, 3)) return;
      if (this.canFinish() && this._finBox && Math.abs(x - this._finBox.x) < this._finBox.w / 2 && Math.abs(y - this._finBox.y) < this._finBox.h / 2) return;
      if (y > 1080 - this.stripZone()) {
        this.scrub = { was: this.playing };
        if (this.playing) this.pause();
        this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); this.ended = false;
        return;
      }
      this.toggle();
    }
    pointerMove(x) { if (this.scrub && this.song) this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); }
    pointerUp() {
      if (!this.scrub) return;
      const was = this.scrub.was; this.scrub = null;
      if (was) this.play(this.pos);
    }

    draw() {
      const docked = UI.docked(), title = (Game.content.jingle && Game.content.jingle.title) || TITLE;
      if (!this.song || !this.nums) {
        NB.ground();
        NB.title(title, 960, 420, { size: 110, t0: this.enteredAt + 0.2, hi: 'Zero' });
        const msg = this.err ? 'the jingle is missing (' + this.err + ')' : 'warming up the synths…';
        NB.write(msg, 960, 580, { font: 'ui', size: 44, color: PN.pencil, align: 'center', t0: this.enteredAt + 0.4 });
        if (docked) { Dock.head(title, ''); Dock.text('jg-wait', [msg], { cls: 'note center' }); this.dockBar(); }
        else this.chrome(null);
        return;
      }
      const t = clamp(this.now(), 0, this.song.dur), gT = T, gBF = BF;
      T = t; BF = Math.floor(t * 8);
      try {
        drawActs(this.song, this.plan, t);
        cast(this.song, this.plan, t, docked);
        if (!docked) band(this.song, t);
      } finally { T = gT; BF = gBF; }
      if (docked) this.dock(t, title); else this.chrome(t);
    }

    /* ---------- the portrait dock: karaoke, controls and the strip below the stage ---------- */
    dock(t, title) {
      const S = this.song, pl = this.plan;
      const mmss = s => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
      Dock.head(title, `${mmss(t)} / ${mmss(S.dur)}`);
      const { cur, next } = lyricAt(S, t);
      const sec = (cur || next || {}).section;
      const lastE = S.items.length ? S.items[S.items.length - 1].e : 0;
      if (cur) {
        const key = cur.i + ':' + cur.s;
        if (!this._dk || this._dk.key !== key) {
          const hk = hookIdx(cur);
          this._dk = { key, words: cur.words.map((w, j) => ({ w: w.w, s: w.s, e: w.e, hook: hk.has(j) })) };
        }
        Dock.karaoke({ key, words: this._dk.words, t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '' });
      } else Dock.karaoke({ key: 'gap', words: [], t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '',
        note: t > lastE && S.items.length ? 'Straight through zero: y = px.' : this.startedOnce || this.frozen ? '♪ ♫ ♪' : 'loading the jingle…' });
      Dock.text('credit', ['♪ ' + CREDIT], { cls: 'small center' });
      if (this.canFinish() && !this.ended) Dock.buttons('jg-fin', [{ label: 'Finish ✓', color: PN.redpen, onClick: () => this.finish() }]);
      if (this.ended && Game.current() === this) {
        Dock.buttons('jg-end', [{ label: '↺ Play it again', onClick: () => this.restart() },
          { label: 'Back to the map', color: PN.ink, onClick: () => this.back() }]);
      }
      Dock.strip({ value: t, total: S.dur, ticks: pl.ticks, label: 'jingle position', onSeek: (v, phase) => this.scrubTo(v, phase) });
      this.dockBar();
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended && !this.scrub) this.pausedMark(540, 150);
    }
    dockBar() {
      if (this.song && !this.ended) {
        Dock.button({ id: 'restart', glyph: '↺', label: 'restart', aria: 'play the jingle from the start', onClick: () => this.restart() });
        Dock.button({ id: 'play', glyph: this.playing ? '❚❚' : '▶', label: this.playing ? 'pause' : 'play', aria: this.playing ? 'pause' : 'play', primary: true, onClick: () => this.toggle() });
      }
      Dock.button({ id: 'map', glyph: '⌂', label: 'map', aria: 'back to the map', onClick: () => this.back() });
    }
    scrubTo(v, phase) {
      if (!this.song) return;
      const pos = clamp(v, 0, this.song.dur - 0.05);
      if (phase === 'start') { this.scrub = { was: this.playing || (this.scrub && this.scrub.was) }; if (this.playing) this.pause(); this.ended = false; }
      this.pos = pos;
      if (phase === 'end') { const was = this.scrub ? this.scrub.was : false; this.scrub = null; if (was) this.play(this.pos); }
    }
    /* the paused ▶: a white wash and an ink circle */
    pausedMark(y, r) {
      g.save(); g.fillStyle = 'rgba(252,252,248,0.55)'; g.fillRect(0, 0, 1920, UI.docked() ? 1080 : BAND_Y); g.restore();
      g.save(); g.fillStyle = PN.ink; g.beginPath(); g.arc(960, y, r, 0, TAU); g.fill();
      g.fillStyle = PN.page; g.beginPath(); g.moveTo(960 - r * 0.3, y - r * 0.52); g.lineTo(960 + r * 0.52, y); g.lineTo(960 - r * 0.3, y + r * 0.52); g.closePath(); g.fill(); g.restore();
    }

    chrome(t) {
      const t0 = this.enteredAt, S = this.song;
      const intro = t == null || (this.plan && this.plan.acts.length > 1 && t < this.plan.acts[1].at);   // the intro page writes the big title itself
      if (!intro) NB.write(TITLE, 190, 124, { size: 46, weight: 700, t0: -1e9 });
      NB.write('♪ ' + CREDIT, 190, 176, { font: 'ui', size: 26, color: PN.pencil, t0: t0 + 0.4, dur: 0.3 });
      const m = UI.hudSlot(0), r = UI.hudSlot(1), pl = UI.hudSlot(2), fx = { r: m.r, fixed: true };
      if (UI.iconButton('jg-map', '⌂', m.x, m.y, fx)) { this.back(); return; }
      if (S) {
        if (UI.iconButton('jg-restart', '↺', r.x, r.y, fx)) this.restart();
        if (UI.iconButton('jg-play', this.playing ? '❚❚' : '▶', pl.x, pl.y, { ...fx, primary: true })) this.toggle();
      }
      if (t == null) return;
      // Finish: once ≥ 90% of the song has been heard (below the HUD, top right)
      this._finBox = null;
      if (this.canFinish() && !this.ended) {
        const w = UI.tap(250), h = UI.tap(84), fy = m.y + m.r + 30 + h / 2, fxp = m.x + m.r - w / 2;
        this._finBox = { x: fxp, y: fy, w, h };
        if (UI.button('jg-finish', 'Finish ✓', fxp, fy, { w, h, size: UI.text(40), primary: true })) { this.finish(); return; }
      }
      const zone = this.stripZone(), touch = zone > 32;
      if (!this.ended) UI.target('jg-strip', 960, 1080 - zone / 2, 1920, zone);
      const sh = touch ? 18 : 10, sy = 1080 - sh;
      g.save();
      g.fillStyle = PN.pencilLight; g.fillRect(0, sy, 1920, sh);
      g.fillStyle = PN.ink; g.fillRect(0, sy, 1920 * t / S.dur, sh);
      g.fillStyle = PN.redpen; this.plan.ticks.forEach(b => g.fillRect(1920 * b / S.dur - 2, sy - 6, 4, sh + 6));
      g.restore();
      if (touch || this.scrub) NB.pencil(clamp(1920 * t / S.dur, 26, 1894), sy, touch ? 0.9 : 0.6);
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended) {
        this.pausedMark(430, 100);
        NB.write(UI.hint('paused · click or Space to play', 'paused · tap to play'), 960, 590, { font: 'ui', size: 44, weight: 700, align: 'center' });
      }
      if (!this.startedOnce && !this.frozen) NB.write('loading the jingle…', 960, 590, { font: 'ui', size: 44, color: PN.pencil, align: 'center', t0: this.enteredAt + 0.3 });
      if (this.ended && Game.current() === this) {
        g.save(); g.fillStyle = 'rgba(252,252,248,0.6)'; g.fillRect(0, 380, 1920, 360); g.restore();
        if (UI.button('jg-again', '↺ Play it again', 700, 560, { w: 440, h: 110, size: 50, secondary: true })) this.restart();
        if (UI.button('jg-back', 'Back to the map', 1220, 560, { w: 440, h: 110, size: 50, primary: true })) this.back();
      }
    }
  }
  Jingle.STEP = STEP;
  Jingle.Song = Song;
  Jingle.plan = plan;
  Jingle.nums = nums;
  Jingle.KEY = KEY;
  Levels.jingle = Jingle;
})();
